import { Add } from '@mui/icons-material';
import { Button, Pagination, Stack, Typography } from '@mui/material';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import handleError from '@fuse/core/errorHandler';
import FuseLoading from '@fuse/core/FuseLoading';
import { selectUser } from 'app/store/userSlice';
import NewsDialog from './NewsDialog';
import NewsItem from './NewsItem';
import { useUser } from 'src/app/hooks/useUser';
import useMessage from 'src/app/hooks/useMessage';
import {
  useDeleteArticleMutation,
  useFetchArticleTestListQuery,
} from 'src/app/services/articlesApi';
import { createPortal } from 'react-dom';
import _ from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import { selectEmployeesTodayBirthdays, selectTopsTodayBirthdays } from 'app/store/birthdaysSlice';
import NewsBirthdaysCard from './NewsBirthdaysCard';

const NewsList = () => {
  const { t } = useTranslation('translations');
  const location = useLocation();
  const navigate = useNavigate();
  const [dataPerPage, setDataPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPost, setCurrentPost] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [articles, setArticles] = useState([]);
  const user = useSelector(selectUser);
  const { isTop, isAdmin, isPR } = useUser();
  const { showMessage } = useMessage();
  const currentLanguage = useSelector((state) => state.i18n.language);
  const todayEmployeesBirthdays = useSelector(selectEmployeesTodayBirthdays);
  const todayTopsBirthdays = useSelector(selectTopsTodayBirthdays);
  const [isBirthdayCardVisible, setIsBirthdayCardVisible] = useState(
    () => localStorage.getItem('isNewsBirthdayVisible') !== 'false'
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageFromUrl = parseInt(searchParams.get('page')) || 1;
    setCurrentPage(pageFromUrl);
  }, [location.search]);

  const { data, isLoading, refetch } = useFetchArticleTestListQuery(
    {
      page: currentPage,
      limit: dataPerPage,
      category: 0,
      lang: currentLanguage,
    },
    {
      refetchOnMountOrArgChange: false,
      skip: !currentLanguage,
    }
  );

  useEffect(() => {
    refetch();
  }, [location.key]);

  const [deleteArticle] = useDeleteArticleMutation();

  useEffect(() => {
    if (data?.items?.length) {
      setArticles(data.items);
    }
  }, [data?.items]);

  const handleReactionArticle = (partOfArticle) => {
    const newArticles =
      articles?.map((el) => {
        if (el?.id === partOfArticle.articleId) {
          const merged = _.mergeWith({}, el, partOfArticle, (objValue, srcValue) => {
            // If it's an array, override completely
            if (Array.isArray(objValue) && Array.isArray(srcValue)) {
              return srcValue;
            }

            // You can also override full nested objects here if needed:
            if (_.isObject(objValue) && _.isObject(srcValue)) return srcValue;
          });
          return merged;
        } else {
          return el;
        }
      }) || [];

    setArticles(newArticles);
  };

  const handleCreatePost = () => {
    setSidebarOpen(true);
    setCurrentPost(null);
    setCurrentPage(1);
  };

  const handleDeletePost = (post) => {
    deleteArticle(post.id)
      .then(() => {
        showMessage({
          message: t('NewsSuccessfullyDeleted'),
          autoHideDuration: 3000,
          variant: 'success',
        });
        refetch();
      })
      .catch(handleError);
  };

  const paginationNumber = [];

  for (let i = 1; i <= Math.ceil((data?.totalItems || 0) / dataPerPage); i++) {
    paginationNumber.push(i);
  }

  const handleChangePagination = (e, value) => {
    setCurrentPage(value);
    const searchParams = new URLSearchParams(location.search);

    if (value === 1) {
      searchParams.delete('page');
    } else {
      searchParams.set('page', value);
    }
    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  const handleEditPost = () => {
    setCurrentPage(1);
  };

  if (isLoading) return <FuseLoading />;

  return (
    <Fragment>
      {(!data?.items || data.items.length === 0) && (
        <Typography variant="h6" className="mt-10 text-center">
          {t('NoNews', "Yangiliklar yo'q ekan")}
        </Typography>
      )}
      {(Array.isArray(todayEmployeesBirthdays) || Array.isArray(todayEmployeesBirthdays)) &&
        (todayEmployeesBirthdays.length > 0 || todayTopsBirthdays.length > 0) &&
        isBirthdayCardVisible && (
          <div className="mb-16">
            <NewsBirthdaysCard
              topsBirthdays={todayTopsBirthdays}
              employeesBirthdays={todayEmployeesBirthdays}
              onClose={() => setIsBirthdayCardVisible(false)}
            />
          </div>
        )}
      {articles?.map((post, index) => (
        <NewsItem
          isMain={index === 0 && currentPage === 1}
          post={post}
          key={post?.id}
          onEdit={handleEditPost}
          onDelete={handleDeletePost}
          user={user}
          refetchList={refetch}
          handleReactionArticle={handleReactionArticle}
        />
      ))}
      <Stack direction="row" spacing={2} justifyContent="center" className="mb-20">
        <Pagination
          className="flex justify-center"
          count={paginationNumber.length}
          page={currentPage}
          onChange={handleChangePagination}
        />
      </Stack>
      <div className="flex justify-center mb-20">
        {(isAdmin || isTop || isPR) && (
          <Button
            onClick={handleCreatePost}
            variant="outlined"
            color="secondary"
            className="text-sm"
          >
            <Add />
            {t('AddNews')}
          </Button>
        )}
      </div>
      {createPortal(
        <NewsDialog
          post={currentPost}
          onOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          refetchList={refetch}
        />,
        document.body
      )}
    </Fragment>
  );
};

export default NewsList;
